import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Avatar } from "../components/Avatar.js";
import { MatchRow } from "../components/MatchRow.js";
import { MemberPicker } from "../components/MemberPicker.js";
import {
  Achievements,
  Form,
  PeopleTable,
  ProfileName,
  RatingCards,
  RatingTrend,
  SectionHead,
  Stats,
} from "../components/profile.js";
import { Button, inputStyle, Notice } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { fetchMatches } from "../lib/matches.js";
import {
  addGuardian,
  fetchGuardians,
  fetchMember,
  fetchMembers,
  removeGuardian,
  renameManagedMember,
} from "../lib/members.js";
import type { Member } from "../lib/types.js";
import { fetchRatings } from "../lib/ratings.js";
import {
  PROFILE_HISTORY,
  formFor,
  opponentsFor,
  partnersFor,
  recordFor,
  winRate,
} from "../lib/stats.js";

/**
 * Another player's profile — the screen behind every name in the app.
 *
 * Everything on it is already public to an active member: the leaderboard is these ratings,
 * and the match log is these matches. Nothing here is a new disclosure, it is the same
 * facts assembled around one person instead of around one evening.
 *
 * Your own is `/me`, which is this plus the controls to change things. Landing here on
 * yourself is normal — you got here by tapping your own row on the board — so it says so
 * and offers the way over rather than redirecting, which would look like a bug.
 */

export function Player() {
  const { id = "" } = useParams();
  const { member: me } = useAuth();
  const isMe = id === me?.id;

  const {
    data: player,
    isPending,
    error,
  } = useQuery({
    queryKey: ["member", id],
    queryFn: () => fetchMember(id),
    enabled: id !== "",
  });

  const { data: ratings = [] } = useQuery({
    queryKey: ["ratings", id],
    queryFn: () => fetchRatings(id),
    enabled: id !== "",
  });

  const { data: page } = useQuery({
    queryKey: ["player-matches", id],
    queryFn: () => fetchMatches({ memberId: id }, { limit: PROFILE_HISTORY }),
    enabled: id !== "",
  });

  const matches = page?.matches ?? [];
  const record = recordFor(matches, id);
  const played = record.won + record.lost;
  const rate = winRate(record);

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pt-6">
        <Notice>Loading…</Notice>
      </main>
    );
  }

  // A member id that resolves to nothing is either a typo in a shared link or somebody
  // this reader may not see. Both get the same sentence; guessing which would leak the
  // difference between "no such player" and "not for you".
  if (error || !player) {
    return (
      <main className="mx-auto max-w-2xl px-4 pt-6">
        <Notice tone="attention">No such player.</Notice>
        <p className="micro mt-4">
          <Link className="underline" to="/board">
            Back to the board
          </Link>
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <header className="mb-2 flex items-center gap-4">
        <Avatar name={player.displayName} url={player.avatarUrl} size={76} />
        <div className="min-w-0">
          <ProfileName name={player.displayName} />
          <div className="font-mono mt-2 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
            {player.managed ? <span>no login</span> : null}
            {player.role !== "player" ? <span>{player.role}</span> : null}
            {player.role !== "player" && player.category ? <span aria-hidden>·</span> : null}
            {player.category ? <span>{player.category}</span> : null}
            {player.status !== "active" ? (
              <span style={{ color: "var(--color-attention)" }}>· {player.status}</span>
            ) : null}
          </div>
        </div>
      </header>

      {isMe ? (
        <p className="micro mb-5">
          This is you, as everyone else sees you.{" "}
          <Link className="underline" to="/me">
            Edit your profile
          </Link>
        </p>
      ) : null}

      {player.managed ? <Guardians child={player} /> : null}

      {ratings.length === 0 ? (
        <Notice>
          No rating yet. It starts at their self-assessed level and moves fast for the first ten
          matches.
        </Notice>
      ) : (
        <RatingCards ratings={ratings} />
      )}

      <RatingTrend memberId={id} ratings={ratings} />

      <Form results={formFor(matches, id)} />

      <Stats
        items={[
          { value: String(played), label: "Played" },
          { value: String(record.won), label: "Won" },
          { value: String(record.lost), label: "Lost" },
          { value: rate === null ? "—" : `${rate}%`, label: "Win rate" },
        ]}
      />

      <Achievements memberId={id} />

      <PeopleTable
        title="Plays with"
        people={partnersFor(matches, id)}
        empty="No doubles partners yet."
      />

      <PeopleTable title="Plays against" people={opponentsFor(matches, id)} empty="Nobody yet." />

      <section className="mt-8">
        <SectionHead
          title="Recent matches"
          aside={
            <Link className="underline" to={`/matches?player=${id}`}>
              All matches
            </Link>
          }
        />
        {matches.length === 0 ? (
          <p className="text-sm text-ink-muted">No matches logged yet.</p>
        ) : (
          matches
            .slice(0, 5)
            .map((match) => <MatchRow key={match.id} match={match} memberId={me?.id} />)
        )}
      </section>

      {matches.length === PROFILE_HISTORY ? (
        <p className="micro mt-6">
          Records and tables are from their last {PROFILE_HISTORY} matches.
        </p>
      ) : null}
    </main>
  );
}

/**
 * Who looks after a child without a login, for the people allowed to know.
 *
 * `guardians` is readable by a child's own guardians and by organisers, and by nobody else
 * — so for everyone else the query comes back empty and this renders nothing, which is the
 * right thing to show them. Organisers can change the list; a guardian or an admin can fix
 * the child's name. Every one of those is checked again by the database.
 */
function Guardians({ child }: { child: Member }) {
  const { member: me } = useAuth();
  const queryClient = useQueryClient();
  const organiser = hasRole(me, "organiser");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const { data: guardians = [] } = useQuery({
    queryKey: ["guardians", child.id],
    queryFn: () => fetchGuardians(child.id),
  });

  // Only needed to offer somebody to add, and only an organiser can add.
  const { data: members = [] } = useQuery({
    queryKey: ["members"],
    queryFn: fetchMembers,
    enabled: organiser,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["guardians", child.id] });
    void queryClient.invalidateQueries({ queryKey: ["children"] });
    void queryClient.invalidateQueries({ queryKey: ["member", child.id] });
    void queryClient.invalidateQueries({ queryKey: ["members"] });
  };

  const add = useMutation({
    mutationFn: (guardianId: string) => addGuardian(child.id, guardianId),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (guardianId: string) => removeGuardian(child.id, guardianId),
    onSuccess: refresh,
  });
  const rename = useMutation({
    mutationFn: (name: string) => renameManagedMember(child.id, name),
    onSuccess: () => {
      setRenaming(null);
      refresh();
    },
  });

  const isGuardian = guardians.some((guardian) => guardian.id === me?.id);
  const canRename = isGuardian || hasRole(me, "admin");
  if (!organiser && !isGuardian) return null;

  const busy = add.isPending || remove.isPending || rename.isPending;
  const failure = add.error ?? remove.error ?? rename.error;

  return (
    <section className="mb-8">
      <div className="micro mb-3">Looked after by</div>

      {guardians.length === 0 ? (
        <p className="text-sm text-ink-muted">
          Nobody yet. An organiser enters them in events until somebody is added.
        </p>
      ) : (
        guardians.map((guardian) => (
          <div
            key={guardian.id}
            className="flex items-center gap-3 border-t-2 py-3"
            style={{ borderColor: "var(--color-line)" }}
          >
            <Avatar name={guardian.displayName} url={guardian.avatarUrl} size={28} />
            <Link to={`/player/${guardian.id}`} className="flex-1 truncate">
              {guardian.displayName}
            </Link>
            {organiser ? (
              <Button tone="quiet" onClick={() => remove.mutate(guardian.id)} disabled={busy}>
                Remove
              </Button>
            ) : null}
          </div>
        ))
      )}

      {/* Behind a button: the picker lists people before anything is typed, and on the
          child's own page that list would sit above everything the page is for. */}
      {organiser ? (
        picking ? (
          <div className="mt-4">
            <MemberPicker
              label="Add a guardian"
              members={members.filter((m) => !m.managed)}
              chosen={guardians.map((guardian) => guardian.id)}
              onPick={(guardianId) => {
                setPicking(false);
                add.mutate(guardianId);
              }}
            />
            <Button tone="quiet" onClick={() => setPicking(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <Button onClick={() => setPicking(true)} disabled={busy}>
              Add a guardian
            </Button>
          </div>
        )
      ) : null}

      {canRename ? (
        renaming === null ? (
          <div className="mt-4">
            <Button tone="quiet" onClick={() => setRenaming(child.displayName)} disabled={busy}>
              Rename {child.displayName}
            </Button>
          </div>
        ) : (
          <form
            className="mt-4 flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate(renaming);
            }}
          >
            <input
              value={renaming}
              onChange={(event) => setRenaming(event.target.value)}
              maxLength={60}
              aria-label={`New name for ${child.displayName}`}
              style={inputStyle}
              autoFocus
            />
            <Button
              tone="primary"
              type="submit"
              className="shrink-0"
              disabled={busy || !renaming.trim()}
            >
              {rename.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              tone="quiet"
              type="button"
              className="shrink-0"
              onClick={() => setRenaming(null)}
              disabled={busy}
            >
              Cancel
            </Button>
          </form>
        )
      ) : null}

      {failure ? <Notice tone="attention">{failure.message}</Notice> : null}
    </section>
  );
}
